import * as React from 'react';
import { Progress } from 'reactstrap';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { getClientEntityCountsAsync } from '../../../ajax/requests';
import { ClientData } from '../../../models';
import { DuplicateGroup, DuplicateOptions, AnalysedGroup, ClientEntityCounts } from './types';
import StyledTable from '../../../components/StyledTable';

interface Props {
    groups: DuplicateGroup[];
    options: DuplicateOptions;
    onComplete: (analysed: AnalysedGroup[]) => void;
}

function pickKeepAndDelete(
    clients: ClientData[],
    counts: Record<string, ClientEntityCounts>,
    rule: DuplicateOptions['deletionRule'],
): { toKeep: ClientData; toDelete: ClientData[] } {
    if (rule === 'fewerEntities') {
        const sorted = [...clients].sort((a, b) => (counts[b.id!]?.total ?? 0) - (counts[a.id!]?.total ?? 0));
        return { toKeep: sorted[0], toDelete: sorted.slice(1) };
    }
    // noEntities: keep the one with most entities; only delete those with 0 entities
    const sorted = [...clients].sort((a, b) => (counts[b.id!]?.total ?? 0) - (counts[a.id!]?.total ?? 0));
    const toKeep = sorted[0];
    const toDelete = sorted.slice(1).filter(c => (counts[c.id!]?.total ?? 0) === 0);
    return { toKeep, toDelete };
}

const AnalyseStep: React.FC<Props> = ({ groups, options, onComplete }) => {
    const t = useTranslations();
    const [analysed, setAnalysed] = React.useState<AnalysedGroup[] | null>(null);

    React.useEffect(() => {
        let cancelled = false;

        async function run() {
            const allClientIds = [...new Set(groups.flatMap(g => g.clients.map(c => c.id!)))];
            const countsArr = await getClientEntityCountsAsync(allClientIds);
            if (cancelled) return;

            const counts: Record<string, ClientEntityCounts> = {};
            countsArr.forEach(c => { counts[c.clientId] = c; });

            const results: AnalysedGroup[] = groups.map(group => {
                const { toKeep, toDelete } = pickKeepAndDelete(group.clients, counts, options.deletionRule);
                return { ...group, counts, toKeep, toDelete };
            });

            setAnalysed(results);
        }

        run();
        return () => { cancelled = true; };
    }, [groups, options.deletionRule]);

    const totalToDelete = React.useMemo(
        () => analysed?.reduce((sum, g) => sum + g.toDelete.length, 0) ?? 0,
        [analysed],
    );

    if (!analysed) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)', alignItems: 'center', padding: 'var(--spacer-L) 0' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{t(TK.clearDuplicatesAnalysing)}</span>
                <Progress animated value={100} style={{ width: '100%', height: '8px' }} />
            </div>
        );
    }

    const groupsWithDeletions = analysed.filter(g => g.toDelete.length > 0);
    const skipped = analysed.filter(g => g.toDelete.length === 0);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-M)' }}>
            <div style={{ display: 'flex', gap: 'var(--spacer-M)', fontSize: '0.9rem', flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--danger-color)' }}>
                    <strong>{totalToDelete}</strong> {t(TK.clearDuplicatesWillDelete)}
                </span>
                {skipped.length > 0 && (
                    <span style={{ color: 'var(--text-muted)' }}>
                        <strong>{skipped.length}</strong> {t(TK.clearDuplicatesGroupsFound)} {t(TK.clearDuplicatesSkipped)}
                    </span>
                )}
            </div>

            {groupsWithDeletions.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 'var(--spacer-L) 0' }}>
                    {t(TK.clearDuplicatesNoResult)}
                </div>
            ) : (
                <div style={{ maxHeight: '380px', overflowY: 'auto' }}>
                    <StyledTable hover size='sm'>
                        <thead>
                            <tr>
                                <th>{t(TK.name)}</th>
                                <th style={{ textAlign: 'right' }}>{t(TK.appointments)}</th>
                                <th style={{ textAlign: 'right' }}>{t(TK.packs)}</th>
                                <th style={{ textAlign: 'right' }}>{t(TK.orders)}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {groupsWithDeletions.map(group => (
                                <React.Fragment key={group.fieldValue}>
                                    <tr style={{ background: 'var(--bg-light)' }}>
                                        <td colSpan={5} style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'monospace', paddingTop: 'var(--spacer-XS)', paddingBottom: 'var(--spacer-XS)' }}>
                                            {group.fieldValue}
                                        </td>
                                    </tr>
                                    {renderClientRow(group.toKeep, group.counts[group.toKeep.id!], t(TK.clearDuplicatesWillKeep), 'var(--appGreen)')}
                                    {group.toDelete.map(c => renderClientRow(c, group.counts[c.id!], t(TK.clearDuplicatesWillDelete), 'var(--danger-color)'))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </StyledTable>
                </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                    disabled={groupsWithDeletions.length === 0}
                    onClick={() => onComplete(groupsWithDeletions)}
                    style={{
                        padding: 'var(--spacer-XS) var(--spacer-S)',
                        borderRadius: '4px',
                        border: 'none',
                        background: groupsWithDeletions.length > 0 ? 'var(--danger-color)' : 'var(--appLigthGrey)',
                        color: 'white',
                        cursor: groupsWithDeletions.length > 0 ? 'pointer' : 'default',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                    }}
                >
                    {t(TK.clearDuplicatesStartExecution)}
                </button>
            </div>
        </div>
    );
};

function renderClientRow(
    client: ClientData,
    counts: ClientEntityCounts | undefined,
    label: string,
    color: string,
) {
    return (
        <tr key={client.id}>
            <td style={{ fontSize: '0.85rem' }}>{client.name}</td>
            <td style={{ textAlign: 'right', fontSize: '0.85rem' }}>{counts?.appointments ?? 0}</td>
            <td style={{ textAlign: 'right', fontSize: '0.85rem' }}>{counts?.packs ?? 0}</td>
            <td style={{ textAlign: 'right', fontSize: '0.85rem' }}>{counts?.orders ?? 0}</td>
            <td>
                <span style={{ fontSize: '0.75rem', color, fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</span>
            </td>
        </tr>
    );
}

export default AnalyseStep;
